import React, { FC, ReactNode, memo } from 'react';

import Decimal from 'decimal.js';

import Typography from '@mui/material/Typography';

import SkeletonLoader from '@components/common/SkeletonLoader';

import { PERCENT_DIVIDER } from '@constants/common/common.constants';

import useStyles from './styles.module';

type WeightInput = {
  weight0: Decimal;
  weight1: Decimal;
};

type WeightOutput = {
  percent0: number;
  percent1: number;
};

const getRoundedWeights = ({ weight0, weight1 }: WeightInput): WeightOutput => {
  if (!weight0 || !weight1) {
    return { percent0: 0, percent1: 0 };
  }
  const percent0 = weight0.mul(PERCENT_DIVIDER);
  const percent1 = weight1.mul(PERCENT_DIVIDER);

  const floored0 = percent0.floor().toNumber();
  const floored1 = percent1.floor().toNumber();

  const total = floored0 + floored1;
  const delta = 100 - total;

  if (delta === 0) {
    return { percent0: floored0, percent1: floored1 };
  }

  const frac0 = percent0.minus(floored0);
  const frac1 = percent1.minus(floored1);

  if (frac0.greaterThan(frac1)) {
    return { percent0: floored0 + delta, percent1: floored1 };
  }
  return { percent0: floored0, percent1: floored1 + delta };
};

interface TokenWeightItemProps {
  value: string | ReactNode;
  symbol: string;
  classNameItem?: string;
}

const TokenWeightItem: FC<TokenWeightItemProps> = ({
  value,
  symbol,
  classNameItem,
}) => {
  const { classes, cx } = useStyles();

  return (
    <div className={cx(classes.box, classNameItem)}>
      <Typography variant="caption1">{symbol}</Typography>
      <Typography variant="caption1" color={(theme) => theme.primary.p3}>
        {value}
      </Typography>
    </div>
  );
};

interface TokenWeightProps {
  token0Weight: Decimal;
  token1Weight: Decimal;
  symbol0: string;
  symbol1: string;
  className?: string;
  classNameItem?: string;
}

const TokenWeight: FC<TokenWeightProps> = ({
  token0Weight,
  token1Weight,
  symbol0,
  symbol1,
  className,
  classNameItem,
}) => {
  const { classes, cx } = useStyles();

  const roundedWeights = getRoundedWeights({
    weight0: token0Weight,
    weight1: token1Weight,
  });

  return (
    <div className={cx(classes.tokensWeight, className)}>
      <TokenWeightItem
        classNameItem={classNameItem}
        value={
          !token0Weight || token0Weight.isNaN() ? (
            <SkeletonLoader className={classes.weight} />
          ) : (
            `${roundedWeights.percent0}%`
          )
        }
        symbol={symbol0}
      />
      <TokenWeightItem
        classNameItem={classNameItem}
        value={
          !token1Weight || token1Weight.isNaN() ? (
            <SkeletonLoader className={classes.weight} />
          ) : (
            `${roundedWeights.percent1}%`
          )
        }
        symbol={symbol1}
      />
    </div>
  );
};

export default memo(TokenWeight);
